import React, { useState } from "react";
import { IconButton, Menu, MenuItem, ListItemIcon, ListItemText } from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";

/**
 * Array-driven row actions menu (the hamburger to the left of a task name).
 *
 * Pass an `actions` array — each entry renders one menu item:
 *   {
 *     key:     unique string,
 *     label:   text shown in the menu,
 *     icon:    optional <Icon /> element,
 *     color:   optional MUI color token / hex for label + icon (e.g. "error.main"),
 *     onClick: (row) => void,
 *     show:    optional boolean (default true) — drop the item without removing it from the array,
 *     divider: optional boolean — render a divider under this item,
 *   }
 *
 * To add future features, just push another object into the array where the
 * menu is built (see TasksListTable `buildRowActions`). No changes needed here.
 */
const TaskTableRowActionsMenu = ({ row, actions = [] }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const visibleActions = actions.filter((action) => action && action.show !== false);
  const hasActions = visibleActions.length > 0;
  const open = hasActions && Boolean(anchorEl);

  const handleOpen = (event) => {
    event.stopPropagation();
    if (!hasActions) return;
    setAnchorEl(event.currentTarget);
  };

  const handleClose = (event) => {
    event?.stopPropagation?.();
    setAnchorEl(null);
  };

  const handleAction = (event, action) => {
    event.stopPropagation();
    setAnchorEl(null);
    action.onClick?.(row);
  };

  if (!hasActions) return null;

  return (
    <>
      <IconButton
        size="small"
        aria-label="Row actions"
        aria-haspopup="true"
        aria-expanded={open ? "true" : undefined}
        onClick={handleOpen}
        sx={{
          p: 0.2,
          color: "text.secondary",
          "&:hover": {
            color: "text.primary",
            bgcolor: (theme) =>
              theme.palette.mode === "dark" ? "rgba(255, 255, 255, 0.08)" : "rgba(0, 0, 0, 0.04)",
          },
        }}
      >
        <MenuIcon sx={{ fontSize: { xs: "1.1rem", md: "1.15rem" } }} />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        onClick={(event) => event.stopPropagation()}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        slotProps={{
          paper: {
            sx: {
              minWidth: 172,
              borderRadius: 1.5,
              mt: 0.5,
              boxShadow: 4,
            },
          },
        }}
      >
        {visibleActions.map((action) => (
          <MenuItem
            key={action.key}
            divider={Boolean(action.divider)}
            onClick={(event) => handleAction(event, action)}
            sx={{ py: 1 }}
          >
            {action.icon && (
              <ListItemIcon sx={{ minWidth: 32, color: action.color || "inherit" }}>
                {action.icon}
              </ListItemIcon>
            )}
            <ListItemText
              primary={action.label}
              primaryTypographyProps={{
                sx: { fontSize: "0.9rem", color: action.color || "text.primary" },
              }}
            />
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

export default TaskTableRowActionsMenu;
